import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Play, Power, PowerOff, Clock, Edit2, Loader2, Truck,
  Settings, Calendar, TrendingUp, Package, CheckCircle2, XCircle
} from 'lucide-react'
import { carriers, Carrier, CarrierStatistics } from '@/lib/api'
import { useState } from 'react'
import ScheduleEditorModal from '@/components/ScheduleEditorModal'
import { useToast } from '@/components/ui/Toast'

export default function CarriersPage() {
  const toast = useToast()
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean
    carrierId: number
    carrierName: string
    currentValue: boolean
  } | null>(null)
  const [scheduleEditorCarrier, setScheduleEditorCarrier] = useState<Carrier | null>(null)

  // Query para la lista de transportistas (rápida, sin estadísticas)
  const carriersQuery = useQuery({
    queryKey: ['carriers'],
    queryFn: () => carriers.list(false),
  })

  // Query para estadísticas (más lenta, carga en segundo plano)
  const statisticsQuery = useQuery({
    queryKey: ['carriers-statistics'],
    queryFn: () => carriers.getStatistics(),
    enabled: !!carriersQuery.data,
    staleTime: 60000,
  })

  const getCarrierStats = (carrierId: number): CarrierStatistics | null => {
    if (statisticsQuery.data?.statistics) {
      return statisticsQuery.data.statistics[carrierId] || null
    }
    return null
  }

  const statusQuery = useQuery({
    queryKey: ['auto-activation-status'],
    queryFn: () => carriers.getAutoActivationStatus(),
  })

  const runActivationMutation = useMutation({
    mutationFn: () => carriers.runAutoActivation(),
    onSuccess: () => {
      toast.success('Activacion automatica iniciada')
      carriersQuery.refetch()
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const toggleAutoActivation = useMutation({
    mutationFn: async (enable: boolean) => {
      if (enable) {
        return carriers.enableAutoActivation()
      } else {
        return carriers.disableAutoActivation()
      }
    },
    onSuccess: () => {
      statusQuery.refetch()
    },
  })

  const updateAutoManagedMutation = useMutation({
    mutationFn: async ({ carrierId, isAutoManaged }: { carrierId: number; isAutoManaged: boolean }) => {
      return carriers.updateAutoManaged(carrierId, isAutoManaged)
    },
    onSuccess: (data) => {
      carriersQuery.refetch()
      setConfirmDialog(null)
      toast.success(data.message || 'Gestion automatica actualizada')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
      setConfirmDialog(null)
    },
  })

  const updateScheduleMutation = useMutation({
    mutationFn: async ({ carrierId, data }: {
      carrierId: number
      data: {
        inactive_start_time?: string
        inactive_end_time?: string
        always_active?: boolean
      }
    }) => {
      return carriers.updateSchedule(carrierId, data)
    },
    onSuccess: () => {
      carriersQuery.refetch()
      setScheduleEditorCarrier(null)
      toast.success('Horario actualizado correctamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const handleAutoManagedClick = (carrier: any) => {
    setConfirmDialog({
      show: true,
      carrierId: carrier.id,
      carrierName: carrier.name,
      currentValue: carrier.is_auto_managed,
    })
  }

  const confirmAutoManagedChange = () => {
    if (confirmDialog) {
      updateAutoManagedMutation.mutate({
        carrierId: confirmDialog.carrierId,
        isAutoManaged: !confirmDialog.currentValue,
      })
    }
  }

  // Calcular estadísticas generales
  const totalCarriers = carriersQuery.data?.carriers?.length || 0
  const activeCarriers = carriersQuery.data?.carriers?.filter((c: any) => c.active).length || 0
  const autoManagedCarriers = carriersQuery.data?.carriers?.filter((c: any) => c.is_auto_managed).length || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
            <Truck className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Transportistas
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Gestion de transportistas con activacion automatica por horarios
            </p>
          </div>
        </div>

        <button
          onClick={() => runActivationMutation.mutate()}
          disabled={runActivationMutation.isPending}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          <Play className={`w-4 h-4 ${runActivationMutation.isPending ? 'animate-pulse' : ''}`} />
          Ejecutar Ahora
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
              <Truck className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Total transportistas</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalCarriers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Activos</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{activeCarriers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Gestion automatica</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{autoManagedCarriers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusQuery.data?.enabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              <Power className={`w-5 h-5 ${statusQuery.data?.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Sistema automatico</p>
              <p className={`text-lg font-bold ${statusQuery.data?.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {statusQuery.data?.enabled ? 'Habilitado' : 'Deshabilitado'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-Activation Control Panel */}
      {statusQuery.data && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${statusQuery.data.enabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                <Settings className={`w-6 h-6 ${statusQuery.data.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Control de Activacion Automatica
                </h2>
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-600 dark:text-slate-400">
                  <span className="flex items-center gap-1">
                    <Truck className="w-4 h-4" />
                    {statusQuery.data.managed_carriers} gestionados
                  </span>
                  {statusQuery.data.ignored_carriers.length > 0 && (
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <XCircle className="w-4 h-4" />
                      {statusQuery.data.ignored_carriers.length} ignorados
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => toggleAutoActivation.mutate(!statusQuery.data.enabled)}
              disabled={toggleAutoActivation.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
                statusQuery.data.enabled
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50'
              }`}
            >
              {statusQuery.data.enabled ? (
                <>
                  <PowerOff className="w-4 h-4" />
                  Deshabilitar
                </>
              ) : (
                <>
                  <Power className="w-4 h-4" />
                  Habilitar
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Carriers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {carriersQuery.isLoading ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
            <p className="text-slate-500 dark:text-slate-400">Cargando transportistas...</p>
          </div>
        ) : carriersQuery.isError ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12">
            <XCircle className="w-8 h-8 text-red-500 mb-3" />
            <p className="text-red-600 dark:text-red-400">Error cargando transportistas</p>
          </div>
        ) : (
          carriersQuery.data?.carriers.map((carrier: any) => {
            const stats = getCarrierStats(carrier.id)

            return (
              <div
                key={carrier.id}
                className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
                  carrier.active
                    ? 'border-emerald-200 dark:border-emerald-800'
                    : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${carrier.active ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                        <Truck className={`w-5 h-5 ${carrier.active ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`} />
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">{carrier.name}</h3>
                    </div>
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${
                        carrier.active
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {carrier.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="p-4 space-y-3">
                  {statisticsQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Cargando estadisticas...</span>
                    </div>
                  ) : stats ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <Package className="w-3.5 h-3.5" />
                          Pedidos ayer
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {stats.orders_previous_business_day}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <TrendingUp className="w-3.5 h-3.5" />
                          Media semanal
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {stats.avg_orders_last_week.toFixed(1)}/dia
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Media mensual
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {stats.avg_orders_per_business_day_last_month.toFixed(1)}/dia
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400 dark:text-slate-500 italic py-2">
                      Estadisticas no disponibles
                    </div>
                  )}

                  {/* Auto-managed toggle */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Gestion automatica</span>
                    <button
                      onClick={() => handleAutoManagedClick(carrier)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        carrier.is_auto_managed
                          ? 'bg-indigo-600'
                          : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          carrier.is_auto_managed ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Schedule Footer */}
                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900/50 rounded-b-xl border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {carrier.schedule?.always_active ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">Activo 24h</span>
                      ) : carrier.schedule ? (
                        <span className="text-slate-600 dark:text-slate-400">
                          Inactivo {carrier.schedule.inactive_start_time} - {carrier.schedule.inactive_end_time}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 italic">Sin horario</span>
                      )}
                    </div>
                    <button
                      onClick={() => setScheduleEditorCarrier(carrier)}
                      className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Editar
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${confirmDialog.currentValue ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30'}`}>
                <Settings className={`w-6 h-6 ${confirmDialog.currentValue ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Confirmar cambio
              </h3>
            </div>

            <p className="text-slate-600 dark:text-slate-400 mb-4">
              ¿Seguro que quieres {confirmDialog.currentValue ? 'desactivar' : 'activar'} la gestion automatica para <strong className="text-slate-900 dark:text-white">{confirmDialog.carrierName}</strong>?
            </p>

            <div className={`mb-6 p-4 rounded-xl text-sm ${
              confirmDialog.currentValue
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800'
            }`}>
              {confirmDialog.currentValue ? (
                <>⚠️ Al desactivar, el transportista NO cambiara de estado segun sus horarios.</>
              ) : (
                <>ℹ️ Al activar, el transportista se activara/desactivara segun sus horarios configurados.</>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDialog(null)}
                disabled={updateAutoManagedMutation.isPending}
                className="px-4 py-2.5 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmAutoManagedChange}
                disabled={updateAutoManagedMutation.isPending}
                className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {updateAutoManagedMutation.isPending ? 'Actualizando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Editor Modal */}
      <ScheduleEditorModal
        carrier={scheduleEditorCarrier}
        onClose={() => setScheduleEditorCarrier(null)}
        onSave={(carrierId, data) => {
          updateScheduleMutation.mutate({ carrierId, data })
        }}
        isSaving={updateScheduleMutation.isPending}
      />
    </div>
  )
}
